import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import { fetchFullStatus, downloadCompletionArchive } from './api';
import { ApplicationFullStatus, StudentLearningStatus, ApplicationLearningSummary } from './types';
import ApplicationCard from './components/ApplicationCard';

const formatDuration = (seconds: number | null) => {
  if (!seconds || seconds === 0) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const parts: string[] = [];
  if (d > 0) parts.push(`${d}д`);
  if (h > 0) parts.push(`${h}ч`);
  if (m > 0) parts.push(`${m}м`);
  if (s > 0 || parts.length === 0) parts.push(`${s}с`);
  return parts.join(' ');
};

const formatDateTime = (dateStr: string | null | undefined) => {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    return d.toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return null;
  }
};

const RiskBadge: React.FC<{ level: string; daysUntil: number | null }> = ({ level, daysUntil }) => {
  const map: Record<string, { bg: string; color: string; label: string }> = {
    overdue: { bg: '#FCDCDC', color: '#EF4444', label: daysUntil !== null ? `Просрочено на ${Math.abs(daysUntil)} дн.` : 'ПРОСРОЧЕНО' },
    at_risk: { bg: '#FCF1DC', color: '#EF7744', label: daysUntil !== null ? `Осталось ${daysUntil} дн.` : 'В ЗОНЕ РИСКА' },
    closed:  { bg: '#DCFCE7', color: '#166534', label: 'ЗАВЕРШЕНО' },
  };
  const s = map[level] ?? { bg: '#EFF4FF', color: '#1E40AF', label: daysUntil !== null ? `Осталось ${daysUntil} дн.` : 'В ПРОЦЕССЕ' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
      background: s.bg, color: s.color, letterSpacing: 0.3, whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
};

const TestBadge: React.FC<{
  passed: boolean;
  bestScore?: number | null;
  passingScore?: number | null;
}> = ({ passed, bestScore, passingScore }) => {
  if (passed) {
    const scoreText = bestScore != null && passingScore != null ? ` (${bestScore}/${passingScore})` : '';
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center',
        fontSize: 12, fontWeight: 600, color: '#1E40AF', background: '#EFF4FF',
        padding: '3px 10px', borderRadius: 20, whiteSpace: 'nowrap',
      }}>
        Сдан{scoreText}
      </span>
    );
  }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      fontSize: 12, fontWeight: 600, color: '#EF4444', background: '#FCDCDC',
      padding: '3px 10px', borderRadius: 20, whiteSpace: 'nowrap',
    }}>
      Не сдан
    </span>
  );
};

const StudentRow: React.FC<{ student: StudentLearningStatus; refreshing: boolean }> = ({ student, refreshing }) => {
  const [isOpen, setIsOpen] = useState(false);

  const completedModules = student.modules?.filter((m) => m.is_completed).length ?? 0;
  const totalModules = student.modules?.length ?? 0;
  const allTestsPassed =
    student.required_tests_passed >= student.required_tests_total && student.required_tests_total > 0;
  const passedTest = student.tests.find((t) => t.is_passed);
  const progressColor = allTestsPassed ? '#1E40AF' : '#EF4444';

  return (
    <>
      {/* Основная строка */}
      <tr
        onClick={() => setIsOpen(!isOpen)}
        style={{
          background: '#ffffff',
          cursor: 'pointer',
          borderBottom: '1px solid #E5E7EB',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => (e.currentTarget.style.background = '#F8FAFF')}
        onMouseLeave={e => (e.currentTarget.style.background = '#ffffff')}
      >
        {/* ФИО обучающегося */}
        <td style={{ padding: '14px 20px', width: '35%' }}>
          <div>
            <div style={{ fontWeight: 600, color: '#1F2937', fontSize: 14 }}>
              {student.full_name}
            </div>
            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
              {student.login}
            </div>
          </div>
        </td>

        {/* Экзамен */}
        <td style={{ padding: '14px 20px', width: '20%' }}>
          <TestBadge
            passed={allTestsPassed}
            bestScore={passedTest?.best_score}
            passingScore={passedTest?.passing_score}
          />
        </td>

        {/* Прогресс (Проценты + Модули) */}
        <td style={{ padding: '14px 20px', width: '20%' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: progressColor }}>
              {Math.round(student.progress_percentage)}%
            </span>
            <span style={{ fontSize: 13, fontWeight: 500, color: '#6B7280' }}>
              ({completedModules} из {totalModules})
            </span>
          </div>
        </td>

        {/* Статус */}
        <td style={{ padding: '14px 20px', width: '19%' }}>
          <RiskBadge level={student.risk_level} daysUntil={student.days_until_deadline} />
        </td>

        {/* Иконка раскрытия — справа */}
        <td style={{ padding: '14px 20px', width: '6%', textAlign: 'right' }}>
          {isOpen
            ? <ExpandLessIcon fontSize="small" style={{ color: '#1E61AF' }} />
            : <ExpandMoreIcon fontSize="small" style={{ color: '#9CA3AF' }} />}
        </td>
      </tr>

      {/* Раскрывающаяся панель — модули */}
      {isOpen && (
        <tr style={{ background: '#F8FAFF' }}>
          <td
            colSpan={5}
            style={{ padding: 0, borderBottom: '1px solid #E5E7EB' }}
          >
            <div style={{ padding: '12px 20px 16px 20px' }}>

              {/* Заголовок секции */}
              <div style={{
                fontSize: 10, fontWeight: 700, color: '#6B7280',
                textTransform: 'uppercase', letterSpacing: 0.8,
                marginBottom: 6,
              }}>
                Модули
              </div>

              {/* Плашка предупреждения о синхронизации */}
              {refreshing && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  marginBottom: 12, padding: '8px 12px',
                  backgroundColor: '#EFF4FF', borderRadius: 6,
                  color: '#1E61AF', fontSize: 12, fontWeight: 500
                }}>
                  <RefreshIcon className="animate-spin" style={{ fontSize: 16 }} />
                  <span>Идет синхронизация с СДО. Актуальная информация по модулям подгружается и скоро обновится...</span>
                </div>
              )}

              {student.modules && student.modules.length > 0 ? (
                <div style={{ opacity: refreshing ? 0.6 : 1, transition: 'opacity 0.3s' }}>
                  {student.modules.map((module, i) => {
                    const startedAt   = formatDateTime((module as any).started_at);
                    const completedAt = formatDateTime((module as any).completed_at);

                    return (
                      <div
                        key={module.platform_module_id}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          padding: '10px 0',
                          borderBottom: i < student.modules!.length - 1 ? '1px solid #E5E7EB' : 'none',
                          gap: 16,
                        }}
                      >
                        {/* Название */}
                        <span style={{
                          fontSize: 13,
                          color: module.is_completed ? '#1F2937' : '#9CA3AF',
                          fontWeight: 400,
                          flex: '1 1 auto',
                          minWidth: 0,
                          lineHeight: 1.5,
                          wordBreak: 'break-word',
                        }}>
                          {module.module_title}
                        </span>

                        {/* Правая часть — сдвинута левее и увеличена для "Затрачено" */}
                        {module.is_completed ? (
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '140px 140px 196px',
                            gap: 12,
                            flexShrink: 0,
                            alignItems: 'center'
                          }}>

                            {/* Начало */}
                            <div style={{ fontSize: 13, whiteSpace: 'nowrap', textAlign: 'left' }}>
                              {startedAt && (
                                <>
                                  <span style={{ color: '#6B7280', fontWeight: 400 }}>Начало: </span>
                                  <span style={{ color: '#6B7280', fontWeight: 400 }}>{startedAt}</span>
                                </>
                              )}
                            </div>

                            {/* Конец */}
                            <div style={{ fontSize: 13, whiteSpace: 'nowrap', textAlign: 'left' }}>
                              {completedAt && (
                                <>
                                  <span style={{ color: '#6B7280', fontWeight: 400 }}>Конец: </span>
                                  <span style={{ color: '#6B7280', fontWeight: 400 }}>{completedAt}</span>
                                </>
                              )}
                            </div>

                            {/* Затрачено */}
                            <div style={{ fontSize: 13, whiteSpace: 'nowrap', textAlign: 'left' }}>
                              <span style={{ color: '#6B7280', fontWeight: 400 }}>Затрачено: </span>
                              <span style={{ color: '#6B7280', fontWeight: 400 }}>
                                {formatDuration(module.time_spent_seconds)}
                              </span>
                            </div>

                          </div>
                        ) : (
                          <div style={{ width: '500px', textAlign: 'right', flexShrink: 0 }}>
                            <span style={{ fontSize: 13, color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                              Не пройден
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ padding: '8px 0', color: '#9CA3AF', fontSize: 13 }}>
                  Модули ещё не изучались
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const ProgramStatPill: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <div style={{ width: 14, height: 14, borderRadius: 5, flexShrink: 0, background: color }} />
    <span style={{ fontSize: 14, color: '#4B5563' }}>{label}</span>
  </div>
);

const ProgramBlock: React.FC<{
  program: ApplicationFullStatus['programs'][number];
  filterStudents: (s: StudentLearningStatus[]) => StudentLearningStatus[];
  refreshing: boolean;
}> = ({ program, filterStudents, refreshing }) => {
  const [collapsed, setCollapsed] = useState(false);

  const students = filterStudents(program.students);

  const noActivity = program.students.filter((s) => s.risk_level === 'no_data').length;
  const completed  = program.students.filter(
    (s) => s.required_tests_passed >= s.required_tests_total && s.required_tests_total > 0
  ).length;
  const atRisk  = program.students.filter((s) => s.risk_level === 'at_risk').length;
  const overdue = program.students.filter((s) => s.risk_level === 'overdue').length;

  const pills: { color: string; label: string }[] = [];
  if (noActivity > 0) pills.push({ color: '#D9D9D9', label: `${noActivity} без активности` });
  if (completed  > 0) pills.push({ color: '#33C000', label: `${completed} завершили` });
  if (atRisk     > 0) pills.push({ color: '#EF7744', label: `${atRisk} в зоне риска` });
  if (overdue    > 0) pills.push({ color: '#EF4444', label: `${overdue} просрочили` });

  return (
    <div style={{
      background: '#ffffff',
      border: '1px solid #E5E7EB',
      borderRadius: 10,
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      {/* Заголовок — кликабельный */}
      <div
        onClick={() => setCollapsed(!collapsed)}
        style={{
          padding: '20px 24px',
          background: '#F9FAFB',
          borderBottom: collapsed ? 'none' : '1px solid #E5E7EB',
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => (e.currentTarget.style.background = '#F1F5F9')}
        onMouseLeave={e => (e.currentTarget.style.background = '#F9FAFB')}
      >
        <div style={{
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: pills.length > 0 ? 12 : 0,
        }}>
          <h3 style={{ fontWeight: 600, fontSize: 18, color: '#1E61AF', margin: 0 }}>
            {program.program_name}
          </h3>
          {collapsed
            ? <ExpandMoreIcon fontSize="small" style={{ color: '#9CA3AF' }} />
            : <ExpandLessIcon fontSize="small" style={{ color: '#9CA3AF' }} />}
        </div>

        {/* Плашки отображаются всегда, даже при collapsed */}
        {pills.length > 0 && (
          <div style={{ display: 'flex', gap: 30, flexWrap: 'wrap' }}>
            {pills.map((p, i) => (
              <ProgramStatPill key={i} color={p.color} label={p.label} />
            ))}
          </div>
        )}
      </div>

      {/* Таблица */}
      {!collapsed && (
        <>
          {students.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '35%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '19%' }} />
                  <col style={{ width: '6%' }} />
                </colgroup>
                <thead>
                  <tr style={{ borderBottom: '1px solid #E5E7EB', background: '#ffffff' }}>
                    {['ФИО обучающегося', 'Экзамен', 'Прогресс', 'Доступ', ''].map((label, idx) => (
                      <th
                        key={idx}
                        style={{
                          padding: '14px 20px',
                          textAlign: idx === 4 ? 'right' : 'left',
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 600,
                          fontSize: 14,
                          color: '#64748B',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {students.map((student) => (
                    <StudentRow
                      key={student.student_id}
                      student={student}
                      refreshing={refreshing}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ padding: 32, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
              Студенты по данному фильтру не найдены
            </div>
          )}
        </>
      )}
    </div>
  );
};

const ApplicationDetailsPage: React.FC<{ onLogout: () => void }> = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const location = useLocation();
  const [data, setData] = useState<ApplicationFullStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');

  const [isDownloadingArchive, setIsDownloadingArchive] = useState(false);

  // Извлекаем переданный ранее summary из истории переходов (роутера)
  const stateSummary = (location.state as { summary?: ApplicationLearningSummary } | null)?.summary;

  const loadData = useCallback(async (force = false) => {
    if (!applicationId) return;
    try {
      if (force) setRefreshing(true);
      else setLoading(true);
      const res = await fetchFullStatus(Number(applicationId), force, true);
      setData(res);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [applicationId]);

  const handleDownloadArchive = async () => {
    if (!applicationId) return;
    try {
      setIsDownloadingArchive(true);
      await downloadCompletionArchive(Number(applicationId));
    } catch (e: any) {
      console.error('Ошибка при скачивании архива:', e);
      alert(`Не удалось скачать архив.\nПричина: ${e.message}`);
    } finally {
      setIsDownloadingArchive(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchInitialAndSync = async () => {
      await loadData(false);

      if (isMounted) {
        await loadData(true);
      }
    };

    fetchInitialAndSync();

    return () => {
      isMounted = false;
    };
  }, [loadData]);

  if (loading || (!data && refreshing)) {
    return (
      <div className="p-20 text-center animate-pulse text-[#1E61AF] font-bold">
        {refreshing ? 'Синхронизация с СДО...' : 'Загрузка детальной аналитики...'}
      </div>
    );
  }

  if (!data) return <div className="p-20 text-center">Данные не найдены</div>;

  const filterStudents = (students: StudentLearningStatus[]) => {
    if (!search.trim()) return students;
    const q = search.toLowerCase();
    return students.filter(
      (s) => s.full_name.toLowerCase().includes(q) || s.login.toLowerCase().includes(q)
    );
  };

  return (
    <div className="w-full p-4 lg:p-8 space-y-8" style={{ boxSizing: 'border-box', minHeight: '100vh' }}>

      {/* Навигация */}
      <div className="flex items-center justify-between">
        <Link
          to="/monitoring"
          className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium group transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-transform"
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Назад к мониторингу
        </Link>

        <div className="flex items-center gap-3">
          {/* Кнопка "Скачать архив" */}
          {String(data.summary.overall_status).toUpperCase() === 'CLOSED' && (
            <button
              onClick={handleDownloadArchive}
              disabled={isDownloadingArchive || refreshing}
              className="flex items-center gap-2 px-6 py-2 bg-white border border-[#E5E7EB] rounded-[10px] text-[#1E61AF] font-semibold text-sm hover:bg-gray-50 transition-all shadow-sm disabled:opacity-50"
            >
              {isDownloadingArchive ? (
                <RefreshIcon className="animate-spin" fontSize="small" />
              ) : (
                <FolderZipIcon fontSize="small" />
              )}
              {isDownloadingArchive ? 'Формирование архива...' : 'Скачать архив'}
            </button>
          )}

          {/* Кнопка "Синхронизировать с СДО" */}
          <button
            onClick={() => loadData(true)}
            disabled={refreshing || isDownloadingArchive}
            className="flex items-center gap-2 px-6 py-2 bg-white border border-[#E5E7EB] rounded-[10px] text-[#1E61AF] font-semibold text-sm hover:bg-gray-50 transition-all shadow-sm disabled:opacity-50"
          >
            <RefreshIcon className={refreshing ? 'animate-spin' : ''} fontSize="small" />
            {refreshing ? 'Обновление данных...' : 'Синхронизировать с СДО'}
          </button>
        </div>
      </div>

      {/* Карточка заявки */}
      {(() => {
        // Если бэкенд на деталке вернул null для ответственного специалиста,
        // безопасно подтягиваем его из переданных в state роутера данных.
        const mergedSummary: ApplicationLearningSummary = {
          ...data.summary,
          specialist_id: data.summary.specialist_id ?? stateSummary?.specialist_id ?? null,
          specialist_full_name: data.summary.specialist_full_name || stateSummary?.specialist_full_name || null,
          specialist_email: data.summary.specialist_email || stateSummary?.specialist_email || null,
        };

        return (
          <ApplicationCard item={mergedSummary} onDetails={() => {}} hideDetails />
        );
      })()}

      {/* Поиск */}
      <div className="flex gap-3">
        <div className="flex-1 relative">
          <SearchOutlinedIcon
            fontSize="small"
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Поиск по имени или логину студента..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white shadow-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Программы обучения */}
      <div className="space-y-8">
        <h2 className="font-semibold text-[24px] text-[#1F2937]">
          Программы обучения
        </h2>

        {data.programs.map((program) => (
          <ProgramBlock
            key={program.program_id}
            program={program}
            filterStudents={filterStudents}
            refreshing={refreshing}
          />
        ))}
      </div>

    </div>
  );
};

export default ApplicationDetailsPage;